import { core } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';

/**
 * 获取当前的地理位置、速度。开启高精度定位，接口耗时会增加，可指定 highAccuracyExpireTime 作为超时时间。
 * 地图相关使用的坐标格式应为 gcj02, 注意此处iOS/android均使用(高德)地图引擎
 */
export type MapLocationOptions = {
  /**
   * 是否返回高度信息，需要较高精确度，会减慢接口返回速度
   */
  altitude?: boolean;
  /**
   * 开启高精度定位
   * @default false
   */
  isHighAccuracy?: boolean;
  /**
   * 定位超时时间(ms), 不提供则无限等待定位结果.
   * @default 30000
   */
  expireTime?: number;

  /**
   * 是否在无权限时，显示系统设置弹框
   * @default false
   */
  isShowOpenSettingWhenDisallowed?: boolean;
};

/**
 * 打开调试模式, 结果返回
 */
export type MapLocationCallbackResult = {
  /**
   * 纬度，范围为 -90~90，负数表示南纬
   */
  latitude: string;
  /**
   * 经度，范围为 -180~180，负数表示西经
   */
  longitude: string;
  /**
   * 速度，单位 m/s
   */
  speed: string;
  /**
   * 位置的精确度
   */
  accuracy: string;
  /**
   * 高度，单位 m
   */
  altitude: string;
  /**
   * 逆地理编码结果
   */
  regeocode: {
    /**
     * 格式化地址
     */
    formattedAddress: string;
    /**
     * 国家
     */
    country: string;

    /**
     * 省/直辖市
     */
    province: string;

    /**
     * 市
     */
    city: string;

    /**
     * 区
     */
    district: string;

    /**
     * 城市编码
     */
    citycode: string;

    /**
     * 区域编码
     */
    adcode: string;

    /**
     * 街道名称
     */
    street: string;

    /**
     * 门牌号
     */
    number: string;

    /**
     * 兴趣点名称
     */
    POIName: string;

    /**
     * 所属兴趣点名称
     */
    AOIName: string;
  };
};

export type MapLocationGetLocationApi = (
  options: MapLocationOptions
) => Promise<MapLocationCallbackResult>;

/**
 * 获取当前的地理位置、速度。开启高精度定位，接口耗时会增加，可指定 expireTime 作为超时时间
 * 1. 如果定位被用户强制关闭, 或者用户点了拒绝, 或者在设置里面禁用了, 返回错误code:`location_permission_disallow`
 * 2. 如果定位超时了, 则返回错误code: `location_service_timeout`
 * 3. 如果定位未决定, 则返回错误code: `location_service_not_determined`(iOS可能弹出来用户很久未点, 会自动抛出系统BUG)
 * 4. 其他错误
 *  AMapLocationErrorUnknown = 1,               ///<未知错误
 *  AMapLocationErrorLocateFailed = 2,          ///<定位错误
 *  AMapLocationErrorReGeocodeFailed  = 3,      ///<逆地理错误
 *  AMapLocationErrorTimeOut = 4,               ///<超时
 *  AMapLocationErrorCanceled = 5,              ///<取消
 *  AMapLocationErrorCannotFindHost = 6,        ///<找不到主机
 *  AMapLocationErrorBadURL = 7,                ///<URL异常
 *  AMapLocationErrorNotConnectedToInternet = 8,///<连接异常
 *  AMapLocationErrorCannotConnectToHost = 9,   ///<服务器连接失败
 *  AMapLocationErrorRegionMonitoringFailure=10,///<地理围栏错误
 *  AMapLocationErrorRiskOfFakeLocation = 11,   ///<存在虚拟定位风险
 *  AMapLocationErrorNoFullAccuracyAuth = 12,   ///<精确定位权限异常
 */
export const getLocation: MapLocationGetLocationApi = ({
  altitude,
  expireTime = 30000,
  isHighAccuracy = false,
  isShowOpenSettingWhenDisallowed = false,
}: MapLocationOptions) => {
  return new Promise<MapLocationCallbackResult>((resolve, reject) => {
    core.call<
      'fabric_map.getLocation',
      MapLocationOptions,
      MapLocationCallbackResult
    >(
      'fabric_map.getLocation',
      {
        altitude,
        expireTime,
        isHighAccuracy,
        isShowOpenSettingWhenDisallowed,
      },
      (result) => {
        const fabricErrors = filterFabricErrors(
          result.code,
          result.message,
          result.data
        );
        if (fabricErrors) {
          reject(fabricErrors);
        } else {
          resolve(result.data);
        }
      }
    );
  });
};
